import React, { useCallback, useMemo, useState, useRef, useEffect } from "react";
import {
  DataTable,
  DateRangePicker,
  type Column,
  type SortingState,
  Button,
  fmt,
  FilterBlock,
  useTableExport,
  useToast,
} from "@racoon/ui";
import { useT, Namespace } from "@racoon/i18n";
import { getEnumValue, toTimestamp } from "@racoon/lib";
import {
  jobGrpc,
  useLazyListJobsQuery,
  useListJobsQuery,
  useListOperationTimeWindowJobsQuery,
} from "~/shared/api/jobs/job.grpc";
import { JobMapTable } from "./job-map/job.map.table";
import { JobMapDateFilter } from "./job-map/job-map-date-filter";
import { JobMapTotals } from "./job-map/job-map-totals";
import { adaptJobs, OrderRow } from "../model/adapters";
import { useJobCard } from "../ui/use-job-card";
import { useCancelJobs } from "../hooks/use-cancel-jobs";
import { OrdersActionsMenu } from "./orders.actions.menu";

import {
  useOrderFilters,
  ORDER_DATE_PRESETS,
  searchRequest,
  isBinQuery,
  photoFilterToRequest,
  isReviewPhotoFilter,
  JOB_STATUS_GROUPS,
  PHOTO_FILTERS,
  ORDER_STATUSES,
  ORDER_VIEWS,
  getStatusJobStatuses,
  statusPillCount,
  DEFAULT_ORDER_DATE_FIELD,
  DEFAULT_JOB_MAP_DATE_FIELD,
  isOrderDateField,
  useOrderDateFieldOptions,
  type OrderDateField,
  type OrderStatusFilter,
  type OrderViewFilter,
} from "../model/use.order.filters";
import { getAuthorName } from "../model/orders.helpers";
import { isJobNotCompleted, isJobRoutable } from "~/shared/lib/job-status";
import { hasSeveralCoJobs } from "~/shared/lib/co-jobs";
import { useOrderColumns } from "../model/use.order.columns";
import { REGISTRY_SORT_BY } from "../model/registry-sort";
import { useOrdersUrlState } from "../model/use.orders.urlState";
import { useRegistryIncidents } from "../model/use-registry-incidents";
import { useListCounterpartiesQuery } from "~/shared/api/organization/organization.grpc";
import {
  COLUMN_FILTERS,
  YES_NO_FILTERS,
  columnFilterOptions,
  hasColumnFilter,
  matchesColumnFilters,
  readColumnFilters,
  type ColumnFilter,
} from "../model/column-filters";
import { freshCheckedJobs, pickCheckedJobs } from "../model/checked-jobs";

import { useTableSettings } from "~/shared/hooks/useTableSettings";
import { useMediaQuery } from "~/shared/hooks/useMediaQuery";
import { useAccumulatedList } from "~/shared/hooks/use-accumulated-list";
// Type for options
type Option<T> = { key: string; value: T };
import { JobType, JobStatus, ConnectProto } from "@racoon/api";

import { useOrderActions } from "../model/use.order.actions";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { useIsOwnOrganizationForWrite } from "~/shared/hooks/use-is-own-organization";
import { useLazyGetRouteQuery } from "~/shared/api/routes/route.grpc";
import { useListPickupLocationGroupsQuery } from "~/shared/api/pickup-location-groups/pickup-location-groups.grpc";
import { useRouteJobsModal } from "~/pages/dashboard/routes/hooks/useRouteJobsModal";
import { formatVolume, getJobFactContainerCount, getJobFactVolume } from "~/shared/lib/job-metrics";
import { useAddJobsToRoutePickerModal } from "~/pages/dashboard/routes/hooks/useAddJobsToRoutePickerModal";
import { useAssignRouteModal } from "~/features/assign-route/hooks/use-assign-route-modal";
import { useCustomerCardModal } from "~/features/customer-card/hooks/use-customer-card-modal";
import { usePickupLocationCard } from "~/features/pickup-location-modal/hooks/use-pickup-location-card";
import { useUserCard } from "~/pages/dashboard/users/ui/use-user-card";
import { useShowOnDashboard } from "~/shared/hooks/useShowOnDashboard";
import { toListSort } from "~/shared/lib/list-sort";

const ORDERS_PAGE_SIZE = 100;
const NO_INCIDENTS: readonly ConnectProto.DriverIncident[] = [];
const noIncidents = () => NO_INCIDENTS;

// A facet counts under every filter but its own, so a pill keeps its badge while selected.
// "totals" is what the whole window adds up to, for the map's totals bar.
const STATUS_COUNTS_LIST_FILTER = { currentPage: 0, pageSize: 1, facets: ["status"] };
const MAP_COUNTS_LIST_FILTER = { currentPage: 0, pageSize: 1, facets: ["status", "totals"] };

/** A category off the URL as a ListJobs filter; "all" narrows nothing. */
const categoryFilter = (category: string) => {
  const number = getEnumValue(ConnectProto.JobCategorySchema, category);
  return number !== undefined ? { jobCategories: [number] } : {};
};

type DayWindow = { startISO: string; endISO: string };

/** The date picker's days as a ListJobs time filter on the given job time column. */
const timeWindow = ({ startISO, endISO }: DayWindow, field: OrderDateField) => ({
  from: toTimestamp(new Date(startISO)),
  to: toTimestamp(new Date(endISO)),
  field,
});

const selectJobRows = (data: ConnectProto.JobList) => data.jobs;
const selectJobKey = (job: ConnectProto.Job) => String(job.id);
const selectListTotal = (data: ConnectProto.JobList) => data.listInfo?.totalItems;

type Props = {
  serviceProviderId: string;
  onRowClick?: (row: OrderRow) => void;
  onExport?: (rows: OrderRow[]) => void;
  highlightOrderId?: string | null;
  unassignedJobCount?: number;
};

export const OrdersTable: React.FC<Props> = ({
  serviceProviderId,
  onRowClick,
  highlightOrderId,
}) => {
  const t = useT();
  const { openJob } = useJobCard();
  const [selectedOrderId, setSelectedOrderId] = useState<string | number | null>(null);
  // Rows checked in the leading checkbox column — the target of the "Действие" menu.
  // The checked orders themselves, not only their ids: a re-sort reloads the list from
  // its first page, and a checked job off that page must still be acted on.
  const [checkedJobList, setCheckedJobList] = useState<ConnectProto.Job[]>([]);
  const isMd = useMediaQuery("(min-width: 768px)");
  const { isReceiverUser, activeCompany } = useAuthSession();

  // 2. GET AI SETTINGS (ai_enabled) and table configuration
  const { settings, columnConfig, handleColumnConfigChange } = useTableSettings(
    "orders",
    serviceProviderId,
  );
  const isAiEnabled = settings?.aiEnabled ?? false;
  // URL state for calendar
  const { startDate, endDate, startISO, endISO, handleDateChange, searchParams, patch } =
    useOrdersUrlState();

  const serviceProviderIdBig = useMemo(() => {
    try {
      return serviceProviderId ? BigInt(serviceProviderId) : undefined;
    } catch {
      return undefined;
    }
  }, [serviceProviderId]);

  // Sync states to URL
  // The top row switches between the two views (pages). Карта заявок is the primary
  // one; the registry is the default only where the map is not offered (mobile).
  const viewValue = searchParams.get("view") || (isMd ? "job-map" : "registry");
  const view = useMemo(
    () => ORDER_VIEWS.find((v) => v.value === viewValue) || ORDER_VIEWS[0],
    [viewValue],
  );
  const setView = (option: Option<OrderViewFilter>) =>
    patch({ view: String(option.value), page: null });
  const isJobMap = view.value === "job-map";

  // The status pills narrow both views down.
  const statusValue = searchParams.get("status") || "all";
  const status = useMemo(
    () => ORDER_STATUSES.find((s) => String(s.value) === statusValue) || ORDER_STATUSES[0],
    [statusValue],
  );
  const setStatus = (value: string) => patch({ status: value, page: null });

  // When we arrive with a highlighted order, force the registry view and the "Все"
  // status pill into the URL so the highlighted row is visible regardless of its own
  // status. Tracked per id so switching afterwards isn't fought by this effect.
  const appliedHighlightRef = useRef<string | null>(null);
  useEffect(() => {
    if (!highlightOrderId) {
      appliedHighlightRef.current = null;
      return;
    }
    if (appliedHighlightRef.current === highlightOrderId) return;
    appliedHighlightRef.current = highlightOrderId;
    if (viewValue !== "registry" || statusValue !== "all")
      patch({ view: "registry", status: "all", page: null });
  }, [highlightOrderId, viewValue, statusValue, patch]);

  // The category tabs narrow both views alike, «Все типы заданий» unless one is picked.
  const jobCategory = searchParams.get("category") || "all";
  const setJobCategory = (value: string) => patch({ category: value, page: null });

  // Which job time column the date picker narrows on. Lives in the URL like every
  // other filter, so a shared link opens the same window the sender saw.
  const dateFieldOf = (view: OrderViewFilter): OrderDateField => {
    const value = searchParams.get("dateField");
    if (isOrderDateField(value)) return value;
    return view === "job-map" ? DEFAULT_JOB_MAP_DATE_FIELD : DEFAULT_ORDER_DATE_FIELD;
  };
  const dateField = dateFieldOf(view.value);

  const setDateField = (value: string) => patch({ dateField: value, page: null });
  const dateFieldOptions = useOrderDateFieldOptions();

  // Statuses the selected pill narrows to, as gRPC enum numbers for the query.
  const tabJobStatusNumbers = useMemo(() => {
    return getStatusJobStatuses(status.value)
      .map((name) => ConnectProto.JobStatusSchema.values.find((v) => v.name === name)?.number)
      .filter((n): n is number => n !== undefined);
  }, [status.value]);

  // The registry's filters, read straight off the URL for the request. useOrderFilters
  // reads the same keys for the controls; the request cannot come from the hook because
  // the hook also needs the response (for its dropdown option lists).
  // Ids parse straight to BigInt: a Number() round-trip would silently corrupt ids
  // above 2^53.
  const urlIdArray = useCallback(
    (key: string) =>
      (searchParams.get(key) ?? "")
        .split(",")
        .filter((part) => /^[1-9]\d*$/.test(part))
        .map(BigInt),
    [searchParams],
  );
  // The statuses the status dropdown narrows to, within the selected pill.
  const dropdownJobStatuses = useMemo(() => {
    const value = searchParams.get("jobStatus") as JobStatus | null;
    return value ? (JOB_STATUS_GROUPS[value] ?? [value]) : undefined;
  }, [searchParams]);

  const requestFilters = useMemo(() => {
    const jobTypeVal = searchParams.get("jobType");
    const jobTypeNumber = jobTypeVal
      ? ConnectProto.JobTypeSchema.values.find((v) => v.name === jobTypeVal)?.number
      : undefined;

    // The status dropdown narrows within the tab: intersect the two. They can
    // contradict; an empty intersection must yield no rows, not all of them.
    const dropdownStatusNumbers = dropdownJobStatuses
      ? dropdownJobStatuses
          .map((name) => ConnectProto.JobStatusSchema.values.find((v) => v.name === name)?.number)
          .filter((n): n is number => n !== undefined)
      : null;
    let jobStatuses = tabJobStatusNumbers;
    if (dropdownStatusNumbers) {
      if (tabJobStatusNumbers.length === 0) {
        jobStatuses = dropdownStatusNumbers;
      } else {
        const tab = new Set(tabJobStatusNumbers);
        const both = dropdownStatusNumbers.filter((n) => tab.has(n));
        jobStatuses = both.length > 0 ? both : [ConnectProto.JobStatus.UNDEFINED];
      }
    }

    const photoVal = searchParams.get("photo");
    const photoOption = photoVal
      ? PHOTO_FILTERS.find((f) => String(f.value) === photoVal) || null
      : null;

    return {
      // A review-photo filter or a column filter the server cannot apply: the whole
      // match is loaded and filtered here.
      filtersLocally:
        isReviewPhotoFilter(photoOption) || hasColumnFilter(readColumnFilters(searchParams)),
      ...(jobStatuses.length > 0 && { jobStatuses }),
      ...(jobTypeNumber !== undefined && { jobTypes: [jobTypeNumber] }),
      ...photoFilterToRequest(photoOption),
      ...(urlIdArray("pickup").length > 0 && {
        pickupLocationIds: urlIdArray("pickup"),
      }),
      ...(urlIdArray("driver").length > 0 && { driverIds: urlIdArray("driver") }),
      ...(urlIdArray("group").length > 0 && {
        pickupLocationGroupIds: urlIdArray("group"),
      }),
    };
  }, [searchParams, dropdownJobStatuses, tabJobStatusNumbers, urlIdArray]);

  // Receiver users are scoped to themselves; the customer filter only means
  // something for provider users.
  const receiverFilterIds = useMemo(
    () => (isReceiverUser ? [] : urlIdArray("receiver")),
    [isReceiverUser, urlIdArray],
  );

  // Main list query — status, category and the date picker's days all filter
  // server-side. Every status pill is bounded by the dates, in both views, so the
  // pills' badges add up to «Все».
  //
  // Filter on the time column picked in the calendar rather than the endpoint's
  // default one. Defaults to the job's operation time window (tw_start_at) in the
  // registry and to the submission date (created_at) on the map.
  const timeFilter = useMemo(
    () => (startDate && endDate ? timeWindow({ startISO, endISO }, dateField) : undefined),
    [startDate, endDate, startISO, endISO, dateField, view.value],
  );

  // The company's side of its jobs: a receiver user sees the jobs done for it.
  const companyFilter = useMemo(
    () =>
      serviceProviderIdBig === undefined
        ? {}
        : isReceiverUser
          ? { serviceReceiverIds: [serviceProviderIdBig] }
          : { serviceProviderIds: [serviceProviderIdBig] },
    [serviceProviderIdBig, isReceiverUser],
  );

  // The search field as list filters (searchRequest). A full BIN is matched against the
  // company's counterparties, loaded only while one is typed; a receiver user's jobs are
  // all its own, so it searches as text.
  const searchText = searchParams.get("search") ?? "";
  const { customers } = useListCounterpartiesQuery(
    { organizationId: serviceProviderIdBig ?? 0n },
    {
      skip: isReceiverUser || serviceProviderIdBig === undefined || !isBinQuery(searchText),
      selectFromResult: ({ data }) => ({
        customers: data?.counterparties.flatMap((c) => (c.organization ? [c.organization] : [])),
      }),
    },
  );
  const searchFilter = useMemo(
    () => searchRequest(searchText, isReceiverUser ? undefined : customers, receiverFilterIds),
    [searchText, isReceiverUser, customers, receiverFilterIds],
  );

  const listJobsArgs = useMemo(
    () => ({
      ...companyFilter,
      ...(receiverFilterIds.length > 0 && { serviceReceiverIds: receiverFilterIds }),
      ...requestFilters,
      ...searchFilter,
      ...categoryFilter(jobCategory),
      // The map lists orders: one job per order, the one whose status is the order's, so
      // its pages, its total, its status pills and their counts all count orders.
      ...(isJobMap && { onePerOrder: true }),
      ...(timeFilter && { timeFilter }),
    }),
    [
      companyFilter,
      receiverFilterIds,
      requestFilters,
      searchFilter,
      jobCategory,
      isJobMap,
      timeFilter,
    ],
  );

  const { filtersLocally, ...listJobsRequest } = listJobsArgs;

  // The registry's column sort, which the server applies to the whole match: the
  // rows arrive one page at a time, so sorting them here would order only those loaded.
  // The map orders its own rows, and a list filtered here, which holds its whole match, is
  // sorted in the table.
  const [registrySorting, setRegistrySorting] = useState<SortingState>([]);
  const registrySort =
    isJobMap || filtersLocally ? undefined : toListSort(registrySorting, REGISTRY_SORT_BY);
  const pagedRequest = registrySort
    ? { ...listJobsRequest, listFilter: registrySort }
    : listJobsRequest;

  // Both views list the same request. Skip it until its dates are set.
  const skipListQuery = serviceProviderIdBig === undefined || !startDate || !endDate;

  // The filters serialized: a change is another list, so the checked orders and the
  // loaded incidents start over. Company and filter switches are both covered.
  const listSignature = [
    serviceProviderId,
    viewValue,
    statusValue,
    jobCategory,
    startISO,
    endISO,
    dateField,
    searchParams.get("jobType") ?? "",
    searchParams.get("jobStatus") ?? "",
    searchParams.get("photo") ?? "",
    searchParams.get("search") ?? "",
    searchParams.get("pickup") ?? "",
    searchParams.get("driver") ?? "",
    searchParams.get("group") ?? "",
    searchParams.get("receiver") ?? "",
  ].join("|");
  // The full request serialized: any change, the sort's included, resets the accumulator
  // to page 0, which replaces the old per-handler page resets.
  const filterSignature = [
    listSignature,
    registrySort?.sortBy ?? "",
    registrySort?.sortDirection ?? "",
  ].join("|");

  const accumulated = useAccumulatedList<ConnectProto.JobList, ConnectProto.Job>({
    useListQuery: useListJobsQuery,
    listEndpoint: jobGrpc.endpoints.listJobs,
    arg: pagedRequest,
    pageSize: ORDERS_PAGE_SIZE,
    filterKey: filterSignature,
    selectRows: selectJobRows,
    selectTotal: selectListTotal,
    selectRowKey: selectJobKey,
    skip: skipListQuery || filtersLocally,
  });

  // The review-status mode fetches the whole match in one unpaginated query and narrows
  // it client-side (filteredOrders below).
  const { data: unpagedJobs = [], isFetching: isFetchingUnpaged } = useListJobsQuery(
    listJobsRequest,
    {
      skip: skipListQuery || !filtersLocally,
      selectFromResult: ({ data, ...rest }) => ({ data: data?.jobs ?? [], ...rest }),
    },
  );

  const data = filtersLocally ? unpagedJobs : accumulated.rows;
  const isFetching = filtersLocally ? isFetchingUnpaged : accumulated.isFetching;

  // The badges: a one-row request with the status facet per view. Both views are
  // counted, whichever is open — each tab's badge is what its view would list — off the
  // list's filters (category and dates included) with the view's own date column and,
  // on the map, one row per order. The map's request also adds up the totals bar.
  const { timeFilter: _window, onePerOrder: _onePerOrder, ...countsFilters } = listJobsRequest;
  const countsRequestOf = (of: OrderViewFilter) => ({
    ...countsFilters,
    ...(of === "job-map" && { onePerOrder: true }),
    timeFilter: timeWindow({ startISO, endISO }, dateFieldOf(of)),
  });
  const skipCounts = serviceProviderIdBig === undefined;
  const { mapFacets } = useListJobsQuery(
    { ...countsRequestOf("job-map"), listFilter: MAP_COUNTS_LIST_FILTER },
    {
      skip: skipCounts,
      selectFromResult: ({ data }) => ({ mapFacets: data?.listInfo?.facets }),
    },
  );
  const { registryFacets } = useListJobsQuery(
    { ...countsRequestOf("registry"), listFilter: STATUS_COUNTS_LIST_FILTER },
    {
      skip: skipCounts,
      selectFromResult: ({ data }) => ({ registryFacets: data?.listInfo?.facets }),
    },
  );
  // Operation time window query — always loaded. It feeds the filter dropdowns' options,
  // which must offer choices beyond the loaded pages.
  const { data: rawMapJobs = [] } = useListOperationTimeWindowJobsQuery(
    {
      serviceProviderIds: serviceProviderIdBig !== undefined ? [serviceProviderIdBig] : [],
    },
    {
      skip: serviceProviderIdBig === undefined,
      selectFromResult: ({ data, ...rest }) => ({
        data: data?.jobs ?? [],
        ...rest,
      }),
    },
  );

  // Dropdown options must offer choices beyond the loaded pages, so they draw on the
  // OTW set as well.
  const filterOptionSource = useMemo(() => [...rawMapJobs, ...data], [rawMapJobs, data]);

  // Client-side filtering stays on the listed rows. The OTW set above is neither
  // bounded by the date picker nor deduplicated against `data`, so filtering over it
  // would put out-of-window jobs — and duplicate ids — into the list.
  const {
    pickupLocations,
    serviceReceivers,
    drivers,
    selectedPickupLocationIds,
    setSelectedPickupLocationIds,
    selectedServiceReceiverIds,
    setSelectedServiceReceiverIds,
    selectedDriverIds,
    setSelectedDriverIds,
    selectedPickupLocationGroupIds,
    setSelectedPickupLocationGroupIds,
    photo,
    setPhoto,
    jobType,
    setJobType,
    jobStatus,
    setJobStatus,
    JOB_TYPE_FILTERS,
    JOB_STATUS_FILTERS,
    JOB_CATEGORIES,
    onSearchChange,
    searchValue,
    filteredOrders,
  } = useOrderFilters(filterOptionSource, searchParams, patch, status, data);

  // Route (pickup location) groups for the filter dropdown. Options come from
  // the dedicated list endpoint (a job only carries the group id, not its name).
  const { data: pickupLocationGroups = [] } = useListPickupLocationGroupsQuery(
    { ownerOrganizationIds: activeCompany?.id ? [BigInt(activeCompany.id)] : [] },
    {
      skip: !activeCompany?.id,
      selectFromResult: ({ data, ...rest }) => ({
        data: data?.pickupLocationGroups ?? [],
        ...rest,
      }),
    },
  );

  // ---- Tabs -----------------------------------------------------------------
  //
  // A badge counts what its tab lists — a view's tab, its selected pill — off the status
  // facet of the view's counts request: the same filters as the list, counted
  // server-side over the whole match rather than the pages loaded so far, and narrowed
  // to the status dropdown's statuses like the list. A missing facet means the counts
  // have not arrived; the badge stays off rather than show a stale 0. So does every
  // badge under a review filter, which narrows the list client-side, past what the
  // counts see. The category tabs carry no badge.
  const mapStatusCounts = mapFacets?.["status"]?.counts;
  const registryStatusCounts = registryFacets?.["status"]?.counts;
  // `{ count }` for a tab or pill of `of`, spread onto it; nothing while counts are unknown.
  const countProps = useCallback(
    (of: OrderViewFilter, pill: OrderStatusFilter) => {
      const counts = of === "job-map" ? mapStatusCounts : registryStatusCounts;
      return counts && !filtersLocally
        ? { count: statusPillCount(counts, pill, dropdownJobStatuses) }
        : {};
    },
    [mapStatusCounts, registryStatusCounts, filtersLocally, dropdownJobStatuses],
  );

  const visibleOrderViews = useMemo(
    () =>
      ORDER_VIEWS.filter((item) => isMd || item.value !== "job-map").map((item) => ({
        ...item,
        ...countProps(item.value, status.value),
      })),
    [isMd, countProps, status.value],
  );

  const statusPills = useMemo(
    () =>
      ORDER_STATUSES.map((s) => ({
        key: s.key,
        value: String(s.value),
        ...countProps(view.value, s.value),
      })),
    [view.value, countProps],
  );

  // ===== FilterBlock wiring =====
  const filterParams = useMemo(() => {
    const pickupOptions = pickupLocations.map((pl) => ({
      id: String(pl.id),
      name: pl.name || `#${pl.id}`,
    }));
    const srOptions = serviceReceivers.map((sr) => ({
      id: String(sr.id),
      name: sr.name || `#${sr.id}`,
    }));
    const driverOptions = drivers.map((driver) => ({
      id: String(driver.id),
      name: `${driver.lastName} ${driver.firstName}`,
    }));
    const photoOptions = PHOTO_FILTERS.map((p) => ({
      id: String(p.value),
      name: p.key,
    }));

    const jobTypeOptions = JOB_TYPE_FILTERS.map((p) => ({
      id: String(p.value),
      name: p.key,
    }));

    const jobStatusOptions = JOB_STATUS_FILTERS.map((p) => ({
      id: String(p.value),
      name: p.key,
    }));

    const routeGroupOptions = pickupLocationGroups.map((g) => ({
      id: String(g.id),
      name: g.name || `#${g.id}`,
    }));

    return [
      {
        key: "pickup_location_id",
        searchable: true,
        label: t(Namespace.Orders, "filters.pickupLocation") ?? "Площадка",
        type: "select" as const,
        options: pickupOptions,
        // tooking first selected value for single-select
        value: selectedPickupLocationIds[0] ? String(selectedPickupLocationIds[0]) : "",
      },
      {
        key: "service_receiver_id",
        searchable: true,
        label: t(Namespace.Orders, "filters.customer") ?? "Заказчик",
        type: "select" as const,
        options: srOptions,
        value: selectedServiceReceiverIds[0] ? String(selectedServiceReceiverIds[0]) : "",
      },
      {
        key: "driver_id",
        searchable: true,
        label: t(Namespace.Orders, "filters.driver") ?? "Водитель",
        type: "select" as const,
        options: driverOptions,
        value: selectedDriverIds[0] ? String(selectedDriverIds[0]) : "",
      },
      {
        key: "pickup_location_group_id",
        searchable: true,
        label: t(Namespace.Orders, "filters.routeGroup") ?? "Маршрутная группа",
        type: "select" as const,
        options: routeGroupOptions,
        value: selectedPickupLocationGroupIds[0] ? String(selectedPickupLocationGroupIds[0]) : "",
      },
      {
        key: "jobType",
        value: String(jobType?.value ?? ""),
        type: "select" as const,
        label: t(Namespace.Common, "type") ?? "Тип",
        options: jobTypeOptions,
      },
      {
        key: "jobStatus",
        value: String(jobStatus?.value ?? ""),
        type: "select" as const,
        label: t(Namespace.Table, "status") ?? "Статус",
        options: jobStatusOptions,
      },
      {
        key: "photo",
        label: t(Namespace.Orders, "filters.photo") ?? "Фото",
        type: "select" as const,
        options: photoOptions,
        value: String(photo?.value ?? ""),
      },
    ];
  }, [
    pickupLocations,
    serviceReceivers,
    drivers,
    pickupLocationGroups,
    selectedPickupLocationIds,
    selectedServiceReceiverIds,
    selectedDriverIds,
    selectedPickupLocationGroupIds,
    photo?.value,
    jobType?.value,
    jobStatus?.value,
    JOB_TYPE_FILTERS,
    JOB_STATUS_FILTERS,
    t,
  ]);

  const handleFilterParamsChange = (key: string, value: string | null) => {
    switch (key) {
      case "pickup_location_id":
        setSelectedPickupLocationIds(value ? [Number(value)] : []);
        break;
      case "service_receiver_id":
        setSelectedServiceReceiverIds(value ? [Number(value)] : []);
        break;
      case "driver_id":
        setSelectedDriverIds(value ? [Number(value)] : []);
        break;
      case "pickup_location_group_id":
        setSelectedPickupLocationGroupIds(value ? [Number(value)] : []);
        break;
      case "jobType": {
        const next = (JOB_TYPE_FILTERS.find((jt) => jt.value === value) as Option<JobType>) ?? null;
        setJobType(next);
        break;
      }
      case "jobStatus": {
        const next =
          (JOB_STATUS_FILTERS.find((js) => String(js.value) === String(value)) as
            | Option<JobStatus>
            | undefined) ?? null;
        setJobStatus(next);
        break;
      }
      case "photo": {
        const next = PHOTO_FILTERS.find((p) => String(p.value) === String(value)) ?? null;
        setPhoto(next);
        break;
      }
      default:
        if ((COLUMN_FILTERS as readonly string[]).includes(key)) {
          patch({ [key]: value || null, page: null });
        }
        break;
    }
  };

  const { handleToggleAiVisibility } = useOrderActions();
  const { cancelJobs } = useCancelJobs();

  const [fetchRoute] = useLazyGetRouteQuery();
  const { openRouteJobs } = useRouteJobsModal();
  const { openAddJobsToRoutePicker } = useAddJobsToRoutePickerModal();
  const { openAssignRoute } = useAssignRouteModal();

  const handleRouteClick = useCallback(
    (routeId: number) => {
      fetchRoute({ id: BigInt(routeId), ownerOrganizationId: BigInt(activeCompany?.id ?? 0) })
        .unwrap()
        .then((route) => {
          if (route) {
            openRouteJobs(route);
          }
        })
        .catch((error) => console.error("Failed to fetch route:", error));
    },
    [fetchRoute, activeCompany, openRouteJobs],
  );

  // Opens the job card on the untouched proto job (same shape the route jobs
  // modal passes), not the adapted row. The card steps through the rows on
  // screen; they are read through a ref so the columns don't rebuild per page.
  // The map table groups, sorts and collapses its own rows, so there the card opens alone.
  const registryRowsRef = useRef<OrderRow[]>([]);
  const handleEditOrder = useCallback(
    (job: ConnectProto.Job) =>
      openJob(job, isJobMap ? [] : registryRowsRef.current.map((row) => row.rawJob)),
    [openJob, isJobMap],
  );

  const handleOrderIdClick = useCallback(
    (row: OrderRow) => handleEditOrder(row.rawJob),
    [handleEditOrder],
  );

  // Every counterparty named in a row opens its own card over the registry;
  // the plate is the exception — its truck is shown on the dashboard map.
  const { openSingle: openCustomerCard } = useCustomerCardModal({
    providerId: serviceProviderId ? Number(serviceProviderId) : undefined,
  });
  const { openLocation, pickupLocationCard } = usePickupLocationCard();
  const { openUser, userCard } = useUserCard();
  const { showTruck } = useShowOnDashboard();

  // The AI toggle writes the job too: another organization's is skipped with the same notice
  const { toast } = useToast();
  const isOwnForWrite = useIsOwnOrganizationForWrite("job");
  const toggleAiVisibility = useCallback(
    (e: React.MouseEvent, row: OrderRow) => {
      if (isOwnForWrite(row.rawJob?.serviceProviderId, row.rawJob?.serviceReceiverId) === true)
        return handleToggleAiVisibility(e, row);
      e.stopPropagation();
      toast({ title: t(Namespace.Orders, "actions.foreignSkipped", { count: 1 }) });
    },
    [isOwnForWrite, handleToggleAiVisibility, toast, t],
  );

  // The listed jobs' incidents, for the registry's Инцидент column: by job, as the
  // order's jobs table reads them, a page at a time. Incidents are the provider's to see:
  // a receiver user reads a Невывоз from the job alone (debt, or its shift's
  // force-completion).
  const incidentsOf = useRegistryIncidents({
    ownerOrganizationId: serviceProviderIdBig ?? 0n,
    jobs: data,
    listKey: listSignature,
    skip: isJobMap || isReceiverUser || serviceProviderIdBig === undefined,
  });

  const incidentsForFilters = isReceiverUser ? noIncidents : incidentsOf;

  // The filters for the columns the server cannot filter by, their options the values the
  // listed jobs have (the whole match while one is set).
  const columnFilters = useMemo(() => readColumnFilters(searchParams), [searchParams]);
  const columnFilterParams = useMemo(() => {
    const labels: Record<ColumnFilter, string> = {
      author: t(Namespace.Orders, "columns.author"),
      ai: t(Namespace.Orders, "filters.aiVisible"),
      incident: t(Namespace.Orders, "columns.incident"),
      containerType: t(Namespace.Orders, "columns.container_type"),
      priority: t(Namespace.Orders, "columns.priority"),
      geozone: t(Namespace.Orders, "columns.geozone"),
      route: t(Namespace.Orders, "columns.route"),
      plate: t(Namespace.Table, "plateNumber"),
      comment: t(Namespace.Orders, "filters.withComment"),
    };
    return COLUMN_FILTERS.map((key) =>
      // A yes/no filter is a checkbox: ticked keeps the «yes» jobs.
      YES_NO_FILTERS.has(key)
        ? {
            key,
            label: labels[key],
            type: "checkbox" as const,
            options: [{ id: "yes", name: t(Namespace.Table, "yes") }],
            value: columnFilters[key],
          }
        : {
            key,
            label: labels[key],
            type: "select" as const,
            searchable: true,
            options: columnFilterOptions(key, data, t, incidentsForFilters),
            value: columnFilters[key],
          },
    );
  }, [t, data, incidentsForFilters, columnFilters]);

  const { columns, compareByPlanStartOrCreated } = useOrderColumns({
    isAiEnabled,
    handleToggleAiVisibility: toggleAiVisibility,
    onRouteClick: handleRouteClick,
    onOrderIdClick: handleOrderIdClick,
    onCustomerClick: openCustomerCard,
    onLocationClick: openLocation,
    onDriverClick: openUser,
    onPlateClick: showTruck,
    incidentsOf: incidentsForFilters,
  });

  // Only the columns the server sorts offer a sort (registrySorting above).
  const registryColumns = useMemo(
    () =>
      columns.map((column) => ({
        ...column,
        sortable: Boolean(column.sortable) && column.id in REGISTRY_SORT_BY,
      })),
    [columns],
  );

  // The listed jobs: the loaded pages as-is, or the whole match filtered here (a review
  // or a column filter).
  const listedJobs = useMemo(
    () =>
      filtersLocally
        ? filteredOrders.filter((job) =>
            matchesColumnFilters(job as ConnectProto.Job, columnFilters, t, incidentsForFilters),
          )
        : data,
    [filtersLocally, filteredOrders, data, columnFilters, t, incidentsForFilters],
  );
  const adaptedRegistryRows = useMemo(() => adaptJobs(listedJobs, t), [listedJobs, t]);
  registryRowsRef.current = adaptedRegistryRows;
  const sortByPlanStartOrCreated = useMemo(() => {
    return (data: OrderRow[]): OrderRow[] => {
      return [...data].sort((a, b) => compareByPlanStartOrCreated(b, a));
    };
  }, [compareByPlanStartOrCreated]);

  const sortedDataForExport = useMemo(() => {
    return sortByPlanStartOrCreated(adaptedRegistryRows);
  }, [adaptedRegistryRows, sortByPlanStartOrCreated]);

  // The registry shows one page but the export must cover the whole match, so it
  // re-runs the same query without list_filter at click time.
  const [triggerListJobs] = useLazyListJobsQuery();
  const fetchAllForExport = useCallback(async () => {
    // filtersLocally is page state, not a request field — strip it before the RPC.
    const { filtersLocally: _clientFlag, ...request } = listJobsArgs;
    const response = await triggerListJobs(request).unwrap();
    return sortByPlanStartOrCreated(adaptJobs(response.jobs ?? [], t));
  }, [triggerListJobs, listJobsArgs, sortByPlanStartOrCreated, t]);

  const { handleExportExcel } = useTableExport({
    data: sortedDataForExport,
    // A list filtered here is already the whole match, unpaginated, so the rows on
    // hand are the complete filtered set; a server re-fetch would export unfiltered jobs.
    ...(filtersLocally ? {} : { getData: fetchAllForExport }),
    columns: getOrdersExportColumns(t),
    reportName: "Заказы",
    startDate: startDate,
    endDate: endDate,
  });

  const handleRowClick = (row: OrderRow) => {
    if (String(selectedOrderId) === String(row.id)) {
      setSelectedOrderId(null);
    } else {
      setSelectedOrderId(row.id);
      onRowClick?.(row);
    }
  };

  // ---- Checkbox selection + "Действие" menu ---------------------------------
  //
  useEffect(() => {
    setCheckedJobList([]);
  }, [listSignature]);

  const checkedJobs = useMemo(() => freshCheckedJobs(checkedJobList, data), [checkedJobList, data]);
  const checkedOrderIds = useMemo(() => checkedJobs.map((job) => String(job.id)), [checkedJobs]);
  const setCheckedOrderIds = useCallback(
    (keys: React.Key[]) => setCheckedJobList(pickCheckedJobs(keys, checkedJobs, data)),
    [checkedJobs, data],
  );

  // Editing is single-job only (the dialog edits one job at a time). Taken off the adapted
  // rows, so the target carries a typed `rawJob` for the dialog — same path the
  // id link in the table uses.
  const editTargetRow = useMemo(
    () => (checkedJobs.length === 1 ? adaptJobs(checkedJobs, t)[0] : undefined),
    [checkedJobs, t],
  );

  const canEditCheckedJob = !!editTargetRow;

  // Bulk writes act on own jobs only: another organization's zone job (or, while the subtree
  // loads, any job not the active company's own) is skipped with a notice.
  const ownCheckedJobs = useMemo(
    () =>
      (checkedJobs as ConnectProto.Job[]).filter(
        (job) => isOwnForWrite(job.serviceProviderId, job.serviceReceiverId) === true,
      ),
    [checkedJobs, isOwnForWrite],
  );
  const notifySkipped = useCallback(() => {
    const skipped = checkedJobs.length - ownCheckedJobs.length;
    if (skipped > 0)
      toast({ title: t(Namespace.Orders, "actions.foreignSkipped", { count: skipped }) });
  }, [checkedJobs.length, ownCheckedJobs.length, toast, t]);

  const handleEditCheckedJob = useCallback(() => {
    if (!editTargetRow) return;
    handleEditOrder(editTargetRow.rawJob);
  }, [editTargetRow, handleEditOrder]);

  // The two route actions both act on the checked rows: putting them on a trip
  // that already exists, or opening "assign route" with them already in it.
  // Either way the job has to still be unassigned — anything past
  // "Создано" already sits on a trip, so a selection that includes one has no
  // valid route action.
  //
  // A Невывоз goes on a trip re-issued, and `ReissueJob` re-issues only its order's current
  // attempt — the one the map and the job picker list (`one_per_order`) — and only with an
  // order number. The registry lists every attempt, so a checked Невывоз is looked up first:
  // one already re-issued (or without a number) keeps both actions disabled, rather than
  // failing part way through re-issuing the selection.
  const checkedOrderNumbers = useMemo(
    () => [
      ...new Set(
        ownCheckedJobs
          .filter((job) => isJobNotCompleted(job.status) && job.orderNumber)
          .map((job) => job.orderNumber!),
      ),
    ],
    [ownCheckedJobs],
  );
  const { currentAttempts } = useListJobsQuery(
    {
      ...companyFilter,
      orderNumbers: checkedOrderNumbers,
      onePerOrder: true,
      listFilter: { currentPage: 0, pageSize: checkedOrderNumbers.length },
    },
    {
      skip: checkedOrderNumbers.length === 0,
      // Not the previous selection's answer while this one loads.
      selectFromResult: ({ currentData }) => ({ currentAttempts: currentData?.jobs }),
    },
  );
  const areCheckedJobsRoutable = useMemo(() => {
    if (ownCheckedJobs.length === 0) return false;
    const currentIds = new Set(currentAttempts?.map((job) => String(job.id)));
    return ownCheckedJobs.every((job) =>
      isJobNotCompleted(job.status) ? currentIds.has(String(job.id)) : isJobRoutable(job.status),
    );
  }, [ownCheckedJobs, currentAttempts]);

  // A СО trip carries one container at a time (construction / multilift trucks),
  // so several СО jobs cannot land on a single existing trip — those have to go
  // through "Создать новый маршрут", which splits them into a trip each.
  const canAddCheckedJobsToRoute = useMemo(() => {
    if (!areCheckedJobsRoutable) return false;
    return !hasSeveralCoJobs(ownCheckedJobs);
  }, [ownCheckedJobs, areCheckedJobsRoutable]);

  const handleAddCheckedJobsToRoute = useCallback(() => {
    if (ownCheckedJobs.length === 0 || !canAddCheckedJobsToRoute) return;
    // The skipped notice comes once the jobs are added, not when the picker opens.
    openAddJobsToRoutePicker({
      jobs: ownCheckedJobs,
      onAdded: () => {
        setCheckedOrderIds([]);
        notifySkipped();
      },
    });
  }, [ownCheckedJobs, canAddCheckedJobsToRoute, openAddJobsToRoutePicker, notifySkipped]);

  const handleCreateRouteFromCheckedJobs = useCallback(() => {
    if (ownCheckedJobs.length === 0 || !areCheckedJobsRoutable) return;
    openAssignRoute({ preselectedJobs: ownCheckedJobs, onCreated: notifySkipped });
  }, [ownCheckedJobs, areCheckedJobsRoutable, openAssignRoute, notifySkipped]);

  const handleCancelCheckedJobs = useCallback(() => {
    // All foreign: nothing to confirm, the notice now; else once the cancel is confirmed.
    if (ownCheckedJobs.length === 0) return notifySkipped();
    cancelJobs(ownCheckedJobs, () => {
      setCheckedOrderIds([]);
      notifySkipped();
    });
  }, [ownCheckedJobs, cancelJobs, notifySkipped]);

  // 3. PERSISTENCE LOGIC

  return (
    <div className="w-full min-w-0 flex flex-col min-h-0 flex-1 gap-4">
      <FilterBlock
        states={visibleOrderViews}
        statesVariant="tab"
        selectedState={view}
        onStateChange={(value) => setView(value as Option<OrderViewFilter>)}
        pills={statusPills}
        selectedPill={String(status.value)}
        onPillChange={setStatus}
        categories={JOB_CATEGORIES}
        selectedCategory={jobCategory}
        onCategoryChange={(value) => setJobCategory(value)}
        statesWithCategories
        additionalButtonsPosition="bottom"
        onSearchChange={onSearchChange}
        searchValue={searchValue}
        searchPlaceholder={t(Namespace.Orders, "searchPlaceholder")}
        // Wide enough for the whole placeholder, however the row is squeezed
        searchClassName="md:min-w-[25rem] md:max-w-lg"
        tightControlsRow
        // The date filter leads the status pills in both views: the pills count inside it.
        pillsRowStart={
          isJobMap ? (
            <JobMapDateFilter
              from={startDate}
              to={endDate}
              onDateChange={handleDateChange}
              dateField={dateField}
              dateFields={dateFieldOptions}
              onDateFieldChange={setDateField}
            />
          ) : (
            <DateRangePicker
              presets={ORDER_DATE_PRESETS}
              initialDateFrom={startDate}
              initialDateTo={endDate}
              dateFields={dateFieldOptions}
              dateField={dateField}
              align="start"
              onUpdate={(values) => {
                if (values.range.from) handleDateChange("startDate", values.range.from);
                if (values.range.to) handleDateChange("endDate", values.range.to);
                if (values.field && values.field !== dateField) setDateField(values.field);
              }}
            />
          )
        }
        // Like the pill counts, the totals cannot see a review filter's client-side narrowing.
        additionalButtonsDiv={
          isJobMap && !filtersLocally && <JobMapTotals totals={mapFacets?.["totals"]?.counts} />
        }
        filterParams={[...filterParams, ...columnFilterParams] as any}
        onFilterParamsChange={handleFilterParamsChange}
        additionalButtons={
          // The map has no checkbox column, so there is nothing for the menu to act on.
          !isJobMap && (
            <OrdersActionsMenu
              selectedCount={checkedOrderIds.length}
              onCancelJobs={handleCancelCheckedJobs}
              onEditJob={handleEditCheckedJob}
              canEditJob={canEditCheckedJob}
              onAddJobsToRoute={handleAddCheckedJobsToRoute}
              canAddJobsToRoute={canAddCheckedJobsToRoute}
              onCreateRoute={handleCreateRouteFromCheckedJobs}
              canCreateRoute={areCheckedJobsRoutable}
            />
          )
        }
        // Excel goes last, after Фильтр (Figma).
        trailingButtons={
          <span className="hidden md:block">
            <Button
              id="orders-export-button"
              size="sm"
              variant="excel"
              className="!m-0"
              onClick={handleExportExcel}
            ></Button>
          </span>
        }
        additionalButtonsVisible={!!selectedOrderId || isMd}
        itemType="orders"
      />

      {isJobMap ? (
        <JobMapTable
          jobs={listedJobs}
          isFetching={isFetching}
          onEndReached={
            !filtersLocally && accumulated.hasMore ? accumulated.loadNextPage : undefined
          }
          serviceProviderId={serviceProviderId}
          onOrderIdClick={handleOrderIdClick}
        />
      ) : (
        <>
          <div className="flex-1 min-h-0 w-full overflow-auto relative rounded-xl">
            <DataTable
              columns={registryColumns}
              data={adaptedRegistryRows}
              // A list filtered here holds its whole match, so it sorts here; the pages
              // arrive sorted.
              manualSorting={!filtersLocally}
              onSortChange={setRegistrySorting}
              // The table remounts with each view switch; keep the sort it had.
              initialSort={
                registrySorting[0] && {
                  id: registrySorting[0].id,
                  dir: registrySorting[0].desc ? "desc" : "asc",
                }
              }
              loading={isFetching && adaptedRegistryRows.length === 0}
              className="min-w-full h-full"
              rowKey={(row) => String(row.id)}
              selectedRowKeys={
                highlightOrderId
                  ? [highlightOrderId]
                  : selectedOrderId
                    ? [String(selectedOrderId)]
                    : []
              }
              onRowClick={handleRowClick}
              selectable={true}
              onSelectionChange={() => {}}
              checkable
              checkedRowKeys={checkedOrderIds}
              onCheckedRowKeysChange={setCheckedOrderIds}
              autoScrollToSelected={true}
              columnConfig={columnConfig}
              onColumnConfigChange={handleColumnConfigChange}
              namespace={Namespace.Orders}
              onEndReached={
                !filtersLocally && accumulated.hasMore ? accumulated.loadNextPage : undefined
              }
              loadingMore={isFetching && adaptedRegistryRows.length > 0}
            />
          </div>
          {pickupLocationCard}
          {userCard}
        </>
      )}
    </div>
  );
};

const getOrdersExportColumns = (t: any): Column<OrderRow>[] => [
  {
    id: "number",
    header: t(Namespace.Orders, "columns.number"),
    accessor: (r) => r.number,
  },
  {
    id: "author",
    header: t(Namespace.Orders, "columns.author"),
    accessor: (r) => getAuthorName(r),
  },
  {
    id: "typeText",
    header: t(Namespace.Orders, "columns.type"),
    accessor: (r) => r.typeText,
  },
  {
    id: "container_type",
    header: t(Namespace.Orders, "columns.container_type"),
    accessor: (r) => r.containertype?.name ?? "-",
  },
  {
    id: "planCount",
    header: t(Namespace.Orders, "columns.planCount"),
    accessor: (r) => r.rawJob.numberOfContainers || "-",
  },
  {
    id: "factCount",
    header: t(Namespace.Orders, "columns.factCount"),
    accessor: (r) => getJobFactContainerCount(r.rawJob) ?? "-",
  },
  {
    id: "factVolume",
    header: t(Namespace.Orders, "columns.factVolume"),
    accessor: (r) => formatVolume(getJobFactVolume(r.rawJob)),
  },
  {
    id: "customer",
    header: t(Namespace.Orders, "columns.customer"),
    accessor: (r) => r.customer,
  },
  {
    id: "objectName",
    header: t(Namespace.Orders, "columns.object"),
    accessor: (r) => r.objectName,
  },
  {
    id: "executor",
    header: t(Namespace.Orders, "columns.executor"),
    accessor: (r) => r.rawJob?.serviceProvider?.name ?? "—",
  },
  {
    id: "priorityText",
    header: t(Namespace.Orders, "columns.priority"),
    accessor: (r) => t(Namespace.Enum, r.priorityText),
  },
  {
    id: "statusText",
    header: t(Namespace.Orders, "columns.status"),
    accessor: (r) => t(Namespace.Enum, r.status),
  },
  {
    id: "geozone",
    header: t(Namespace.Orders, "columns.geozone"),
    accessor: (r) => r.geozone,
  },
  {
    id: "route",
    header: t(Namespace.Orders, "columns.route"),
    accessor: (r) => r.route,
  },
  {
    id: "createdAt",
    header: t(Namespace.Orders, "columns.createdAt"),
    accessor: (r) => (r.job?.tw_start_at ? fmt.dateTime(r.job.tw_start_at) : "—"),
  },
  {
    id: "finishedAt",
    header: t(Namespace.Orders, "columns.finishedAt"),
    accessor: (r) => (r.finishedAt ? fmt.dateTime(r.finishedAt) : "—"),
  },
  {
    id: "hasPhotoReport",
    header: t(Namespace.Orders, "columns.photo"),
    accessor: (r) => (r.hasPhotoReport ? "Да" : "Нет"),
  },
  {
    id: "tariff",
    header: t(Namespace.Orders, "columns.tariff"),
    accessor: (r) => r.tariff,
  },
  {
    id: "plateNumber",
    header: t(Namespace.Table, "plateNumber"),
    accessor: (r) => (r.driver?.truck as any)?.plateNumber || "-",
  },
  {
    id: "driverName",
    header: t(Namespace.Table, "driver"),
    accessor: (r) => {
      const d = r.driver?.driver as any;
      return d ? `${d.lastName ?? ""} ${d.firstName ?? ""}`.trim() || "-" : "-";
    },
  },
  {
    id: "comment",
    header: t(Namespace.Table, "comment"),
    accessor: (r) => r.comment || "-",
  },
];
